/**
 * BenchRegret — Optimal Lineup Analyser (v2 redesign)
 *
 * Dark regret card (big red mono number) + regret-by-week bar chart,
 * "Worst call of the week" started-vs-benched row with a nudge strip,
 * then the full slot-by-slot lineup vs JAI's optimal.
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, StatStrip, JaiSummaryCard, JaiSuggestionStrip } from '../components/JaiV2'
import PlayerPhoto from '../components/PlayerPhoto'
import { JAIMascot } from '../components/JAIMascot'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const PREVIEW_WEEKS = [
  { label: 'Week 14 (current)', value: 14 },
  { label: 'Week 13', value: 13 },
  { label: 'Week 12', value: 12 },
  { label: 'Week 11', value: 11 },
]

// Regret history for the bar chart (oldest → newest)
const PREVIEW_REGRET_BY_WEEK = [
  ['W10', 8.2], ['W11', 21.4], ['W12', 4.1], ['W13', 12.6], ['W14', 33.6],
]

const PREVIEW_LINEUP = {
  week: 14,
  myScore: 184.6,
  optimalScore: 218.2,
  pointsLeft: 33.6,
  jaiGrade: 'B+',
  slots: [
    {
      pos: 'PG', slot: 1,
      started: { name: 'Stephen Curry',   pts: 39.5 },
      optimal: { name: 'Stephen Curry',   pts: 39.5 },
      swap: false, diff: 0,
    },
    {
      pos: 'PG', slot: 2,
      started: { name: 'Fred VanVleet',   pts: 18.2 },
      optimal: { name: 'Tyrese Maxey',    pts: 41.5 },
      swap: true, diff: -23.3,
    },
    {
      pos: 'SG', slot: 1,
      started: { name: 'Donovan Mitchell', pts: 35.0 },
      optimal: { name: 'Donovan Mitchell', pts: 35.0 },
      swap: false, diff: 0,
    },
    {
      pos: 'SF', slot: 1,
      started: { name: 'Jayson Tatum',    pts: 62.0 },
      optimal: { name: 'Jayson Tatum',    pts: 62.0 },
      swap: false, diff: 0,
    },
    {
      pos: 'PF', slot: 1,
      started: { name: 'Jaren Jackson Jr', pts: 28.7 },
      optimal: { name: 'Jaren Jackson Jr', pts: 28.7 },
      swap: false, diff: 0,
    },
    {
      pos: 'C',  slot: 1,
      started: { name: 'Bam Adebayo',     pts: 41.0 },
      optimal: { name: 'Nikola Jokić',    pts: 51.3 },
      swap: true, diff: -10.3,
    },
    {
      pos: 'UTIL', slot: 1,
      started: { name: 'Shai Gilgeous-Alexander', pts: 44.2 },
      optimal: { name: 'Shai Gilgeous-Alexander', pts: 44.2 },
      swap: false, diff: 0,
    },
  ],
  jaiComment: "Two costly starts this week. Jokić had a monster 51-point night on your bench while you started Bam — a difference of 10.3 fantasy points. The bigger miss was Tyrese Maxey's 41.5-point explosion that you left on pine in favour of Fred VanVleet. JAI flagged Maxey as the preferred start on Tuesday — next time, pull the trigger.",
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function BenchRegret() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? { name: 'The Hardwood Cartel' } : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)
  const [selectedWeek, setSelectedWeek] = useState(14)
  const lineup = PREVIEW_LINEUP

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  const efficiency = ((lineup.myScore / lineup.optimalScore) * 100).toFixed(0)
  const worstCall  = [...lineup.slots].sort((a, b) => a.diff - b.diff)[0]
  const maxRegret  = Math.max(...PREVIEW_REGRET_BY_WEEK.map(([, v]) => v))
  const grid = '52px 1fr 64px 1fr 64px'

  return (
    <AppShell>
      <V2Page
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · Lineup accountability` : 'Lineup accountability'}
          </Link>
        }
        title="Bench Regret"
        sub="Points you left on the bench — and the calls that cost you."
        maxWidth={880}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <select
              value={selectedWeek}
              onChange={e => setSelectedWeek(Number(e.target.value))}
              style={{
                background: 'white', color: V2.navy, border: `1px solid ${V2.hair}`,
                borderRadius: 10, padding: '9px 13px', fontFamily: JAI_FONTS.tight,
                fontSize: 12.5, fontWeight: 600, cursor: 'pointer', outline: 'none',
              }}
            >
              {PREVIEW_WEEKS.map(w => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
            <StatStrip cells={[[lineup.jaiGrade, 'JAI grade', true], [`${efficiency}%`, 'Efficiency']]} />
          </div>
        }
      >

        {/* ── Dark regret card + regret-by-week chart ──────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'stretch', marginBottom: 16 }}>
          <div style={{ background: V2.darkHero, borderRadius: 16, padding: '22px 22px', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.45)', fontFamily: JAI_FONTS.mono, marginBottom: 10 }}>
              WEEK {lineup.week} REGRET
            </div>
            <div style={{ fontSize: 44, fontWeight: 800, fontFamily: JAI_FONTS.mono, color: V2.errorOnDark, letterSpacing: '-0.03em', lineHeight: 1 }}>
              −{lineup.pointsLeft.toFixed(1)}
            </div>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', marginTop: 8, lineHeight: 1.5 }}>
              points left on your bench. You scored {lineup.myScore.toFixed(1)} — JAI's optimal lineup scored {lineup.optimalScore.toFixed(1)}.
            </div>
          </div>

          <V2Card pad="18px 22px">
            <div style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, marginBottom: 14 }}>Regret by week</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 110 }}>
              {PREVIEW_REGRET_BY_WEEK.map(([w, v]) => (
                <div key={w} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: v > 20 ? V2.error : V2.faint, fontFamily: JAI_FONTS.mono }}>−{v}</span>
                  <div style={{ width: '100%', height: Math.max((v / maxRegret) * 78, 4), borderRadius: '5px 5px 2px 2px', background: v > 20 ? V2.error : 'rgba(214,58,58,0.3)' }} />
                  <span style={{ fontSize: 10.5, color: V2.faint, fontFamily: JAI_FONTS.mono }}>{w}</span>
                </div>
              ))}
            </div>
          </V2Card>
        </div>

        {/* ── Worst call of the week ───────────────────────────────────── */}
        {worstCall && worstCall.swap && (
          <V2Card pad="16px 20px" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.error, marginBottom: 12 }}>
              Worst call of the week
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                <PlayerPhoto name={worstCall.started.name} size={38} rounded="full" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: V2.navy }}>Started: {worstCall.started.name}</div>
                  <div style={{ fontSize: 12, color: V2.error, fontFamily: JAI_FONTS.mono, marginTop: 1 }}>{worstCall.started.pts.toFixed(1)} FPTS</div>
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, color: V2.error, fontFamily: JAI_FONTS.mono, letterSpacing: '0.06em', flexShrink: 0 }}>OVER ▸</span>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 11, justifyContent: 'flex-end', minWidth: 0 }}>
                <div style={{ textAlign: 'right', minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: V2.navy }}>Benched: {worstCall.optimal.name}</div>
                  <div style={{ fontSize: 12, color: V2.success, fontFamily: JAI_FONTS.mono, marginTop: 1, fontWeight: 700 }}>{worstCall.optimal.pts.toFixed(1)} FPTS</div>
                </div>
                <PlayerPhoto name={worstCall.optimal.name} size={38} rounded="full" />
              </div>
            </div>
          </V2Card>
        )}

        {/* ── JAI's read ───────────────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <JaiSummaryCard headline={`Lineup grade: ${lineup.jaiGrade} — ${efficiency}% of your optimal score.`}>
            {lineup.jaiComment}
          </JaiSummaryCard>
        </div>

        {/* ── Slot-by-slot table ───────────────────────────────────────── */}
        <V2Card pad="0" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '11px 20px', borderBottom: '1px solid rgba(10,31,68,0.07)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', color: V2.faint, fontFamily: JAI_FONTS.mono, textTransform: 'uppercase' }}>
            <span>Slot</span><span>You started</span><span style={{ textAlign: 'right' }}>FPTS</span><span style={{ textAlign: 'right' }}>JAI optimal</span><span style={{ textAlign: 'right' }}>Δ</span>
          </div>
          {lineup.slots.map((slot, i) => (
            <div
              key={`${slot.pos}-${slot.slot}`}
              style={{
                display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center',
                padding: '11px 20px',
                borderBottom: i < lineup.slots.length - 1 ? '1px solid rgba(10,31,68,0.05)' : 'none',
                background: 'white', boxShadow: slot.swap ? 'inset 3px 0 0 #D63A3A' : 'none',
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono }}>{slot.pos}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: V2.navy }}>{slot.started.name}</span>
              <span style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: V2.navy, fontFamily: JAI_FONTS.mono }}>{slot.started.pts.toFixed(1)}</span>
              <span style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: slot.swap ? V2.error : V2.success }}>
                {slot.swap ? slot.optimal.name : 'Correct call'}
              </span>
              <span style={{ textAlign: 'right', fontSize: 13, fontWeight: 800, color: slot.swap ? V2.error : V2.faint, fontFamily: JAI_FONTS.mono }}>
                {slot.swap ? slot.diff.toFixed(1) : '—'}
              </span>
            </div>
          ))}
        </V2Card>

        {/* ── Nudge strip ──────────────────────────────────────────────── */}
        <JaiSuggestionStrip actionLabel="Ask JAI →" onAction={() => window.location.assign('/chat')}>
          JAI flags better starts before lineups lock — ask "Who should I start tonight?" any game day.
        </JaiSuggestionStrip>

      </V2Page>
    </AppShell>
  )
}
